import { Spinner } from "components/ui/spinner";
import { TooltipLabel } from "components/ui/tooltip-label";
import React from "react";
import { gapColorClass, gapLabel, projectedTooltip } from "./goal-progress-gap";
import {
    TrackedMetric,
    trackedMetricLabels,
    useMetricProgress,
} from "./use-metric-progress";

export type MetricProgressTableDensity = "comfortable" | "compact";

interface DensityStyle {
    headerPaddingBottom: string;
    rowPadding: string;
    loadingPadding: string;
    errorPadding: string;
    labelText: string;
    valueText: string;
    gapText: string;
}

const densityStyles: Record<MetricProgressTableDensity, DensityStyle> = {
    comfortable: {
        headerPaddingBottom: "pb-2.5",
        rowPadding: "py-3.5",
        loadingPadding: "py-6",
        errorPadding: "py-4",
        labelText: "font-semibold text-zinc-800",
        valueText: "text-[15px] font-bold text-zinc-900",
        gapText: "text-[11.5px]",
    },
    compact: {
        headerPaddingBottom: "pb-2",
        rowPadding: "py-2.5",
        loadingPadding: "py-4",
        errorPadding: "py-3",
        labelText: "text-sm font-semibold text-zinc-800",
        valueText: "text-sm font-bold text-zinc-900",
        gapText: "text-[11px]",
    },
};

const MetricProgressRow: React.FC<{
    metric: TrackedMetric;
    density: MetricProgressTableDensity;
}> = ({ metric, density }) => {
    const style = densityStyles[density];
    const { isLoading, isError, progress } = useMetricProgress(metric);

    if (isLoading) {
        return (
            <tr>
                <td colSpan={4} className={`text-center ${style.loadingPadding}`}>
                    <Spinner />
                </td>
            </tr>
        );
    }
    if (isError || !progress) {
        return (
            <tr>
                <td
                    colSpan={4}
                    className={`text-center text-sm text-red-600 ${style.errorPadding}`}>
                    We couldn&apos;t load this row. Please try again.
                </td>
            </tr>
        );
    }

    const { completed, target, projected } = progress;

    return (
        <tr className="border-b border-gray-100 last:border-b-0">
            <td className={`pr-2 ${style.rowPadding}`}>
                <div className={`flex items-center gap-2 ${style.labelText}`}>
                    {trackedMetricLabels[metric]}
                </div>
            </td>
            <td
                className={`w-px whitespace-nowrap pl-4 text-right md:pl-8 ${style.rowPadding}`}>
                <span className={style.valueText}>{completed}</span>
            </td>
            <td
                className={`w-px whitespace-nowrap pl-4 text-right md:pl-8 ${style.rowPadding}`}>
                <span className={style.valueText}>{target}</span>
            </td>
            <td className={`w-px pl-4 text-right md:pl-8 ${style.rowPadding}`}>
                <span className={`whitespace-nowrap ${style.valueText}`}>
                    {projected}
                </span>
                <div
                    className={`md:whitespace-nowrap font-semibold ${style.gapText} ${gapColorClass(projected, target)}`}>
                    {gapLabel(projected, target)}
                </div>
            </td>
        </tr>
    );
};

export const MetricProgressTable: React.FC<{
    metrics: TrackedMetric[];
    density: MetricProgressTableDensity;
}> = ({ metrics, density }) => {
    const headerPaddingBottom = densityStyles[density].headerPaddingBottom;
    return (
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        <th className={`py-0 pr-2 font-semibold ${headerPaddingBottom}`}>
                            Metric
                        </th>
                        <th
                            className={`w-px whitespace-nowrap py-0 pl-4 text-right font-semibold md:pl-8 ${headerPaddingBottom}`}>
                            Completed
                        </th>
                        <th
                            className={`w-px whitespace-nowrap py-0 pl-4 text-right font-semibold md:pl-8 ${headerPaddingBottom}`}>
                            Goal
                        </th>
                        <th
                            className={`w-px whitespace-nowrap py-0 pl-4 font-semibold md:pl-8 ${headerPaddingBottom}`}>
                            <span className="flex items-center justify-end gap-1">
                                Projected
                                <TooltipLabel align="end" tooltip={projectedTooltip} />
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {metrics.map(metric => (
                        <MetricProgressRow
                            key={metric}
                            metric={metric}
                            density={density}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};
